import React, { useState } from 'react';

import { IconSearch } from 'lib/icons';
import { useShareForwardedRef, useWindowSize } from 'lib/utilities';
import PropTypes from 'prop-types';

import { isMobileScreen } from './../../core';
import { INPUT_ICON_POSITIONS, INPUT_SIZES, moveInputCursorToStart } from './../../input';
import { SearchInputClearButton, SearchInputInput } from '../blocks';
import { EXPAND_BEHAVIORS } from '../constants';
import { SearchInputContainer, SearchInputIcon, SearchInputIndicator } from '../elements';

const SearchInput = React.forwardRef(
  (
    {
      clearButtonLabel = 'Clear search input',
      dataTestId = undefined,
      disableBrowserAutocomplete = false,
      expandBehavior = EXPAND_BEHAVIORS.SLIDE_RIGHT,
      id,
      isAlwaysExpanded = false,
      name,
      onBlur = () => {},
      onChange,
      onFocus = () => {},
      placeholder = '',
      size = INPUT_SIZES.STANDARD,
      value,
      ...other
    },
    ref
  ) => {
    const [isExpanded, setIsExpanded] = useState(isAlwaysExpanded);
    const inputRef = useShareForwardedRef(ref);
    const windowSize = useWindowSize();

    const isFilled = !!value;
    const isMobileView = isMobileScreen(windowSize.width);

    const handleClick = () => {
      setIsExpanded(true);
    };

    const handleBlur = ({ target }) => {
      if (isMobileView) {
        onFocus(target.value);
      }

      moveInputCursorToStart(target, value);
      setIsExpanded(false);
      onBlur();
    };

    const handleChange = ({ target }) => {
      onChange(target.value);
    };

    const handleClear = () => {
      onChange('');
      if (inputRef && inputRef.current) {
        inputRef.current.focus();
      }
    };

    const handleFocus = (event) => {
      onFocus(event);
      if (isMobileView) {
        onFocus(true);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === ' ') {
        if (isExpanded === false) {
          event.preventDefault();
          setIsExpanded(true);
        }
      }
      if (event.key === 'Enter') {
        setIsExpanded(true);
      }
    };

    return (
      <SearchInputContainer
        data-testid={dataTestId ? `${dataTestId}-wrapper` : undefined}
        onKeyDown={handleKeyDown}
        {...{ expandBehavior, isExpanded }}
        {...other}
      >
        <SearchInputInput
          autoComplete={disableBrowserAutocomplete ? 'off' : ''}
          data-testid={dataTestId}
          onBlur={handleBlur}
          onClick={handleClick}
          onChange={handleChange}
          onFocus={handleFocus}
          type="text"
          {...{ expandBehavior, id, isAlwaysExpanded, isExpanded, isFilled, placeholder, name, size, value }}
          ref={inputRef}
        />
        <SearchInputIndicator expandBehavior={expandBehavior} size={size} />
        <SearchInputIcon
          icon={<IconSearch />}
          position={INPUT_ICON_POSITIONS.TRAILING}
          {...{ expandBehavior, isAlwaysExpanded, isFilled }}
        />
        <SearchInputClearButton
          data-testid={dataTestId ? `${dataTestId}-clear-input-button` : undefined}
          clearButtonLabel={clearButtonLabel}
          isVisible={isFilled}
          onClick={handleClear}
        />
      </SearchInputContainer>
    );
  }
);

SearchInput.propTypes = {
  /** Label of the clear input button */
  clearButtonLabel: PropTypes.node,
  /** Allows disabling of browser-based Autocomplete (true == disabled) */
  disableBrowserAutocomplete: PropTypes.bool,
  /** Id value used for testing */
  dataTestId: PropTypes.string,
  /** Defines Search Input expanding behaviour */
  expandBehavior: PropTypes.oneOf(Object.values(EXPAND_BEHAVIORS)),
  /** Identifier of the input component */
  id: PropTypes.string.isRequired,
  /** If true, search input is always expanded */
  isAlwaysExpanded: PropTypes.bool,
  /** Name of the input */
  name: PropTypes.string.isRequired,
  /** Callback to be called when input is out of focus */
  onBlur: PropTypes.func,
  /** Callback to be called when input's value is being changed by user interaction */
  onChange: PropTypes.func.isRequired,
  /** Callback to be called when input is focused */
  onFocus: PropTypes.func,
  /** Text to be displayed when input is empty */
  placeholder: PropTypes.node,
  /** Set the size of the input */
  size: PropTypes.oneOf(Object.values(INPUT_SIZES)),
  /** Current value of the input field */
  value: PropTypes.string.isRequired,
};

export { SearchInput };
